import Splide from "@splidejs/splide";

const SPLIDE_SELECTOR = ".recipe__splide";
const META_SWITCHING_CLASS = "is-recipe-meta-switching";

function updateMeta(root: HTMLElement, focusedSlide: HTMLElement | null) {
  const meta = root.querySelector<HTMLElement>("[data-recipe-meta]");
  if (!meta || !focusedSlide) return;

  const titleEl = meta.querySelector<HTMLElement>("[data-recipe-meta-title]");
  const descriptionEl = meta.querySelector<HTMLElement>("[data-recipe-meta-description]");
  const linkEl = meta.querySelector<HTMLAnchorElement>("[data-recipe-meta-link]");
  const slideLink = focusedSlide.querySelector<HTMLAnchorElement>(".recipe__slide-link");
  if (!titleEl || !descriptionEl || !linkEl || !slideLink) return;

  const nextTitle = slideLink.dataset.recipeTitle ?? "";
  const nextDescription = slideLink.dataset.recipeDescription ?? "";
  const nextHref = slideLink.getAttribute("href") ?? "#";

  if (
    titleEl.textContent === nextTitle &&
    descriptionEl.textContent === nextDescription &&
    linkEl.getAttribute("href") === nextHref
  ) {
    return;
  }

  meta.classList.add(META_SWITCHING_CLASS);
  window.setTimeout(() => {
    titleEl.textContent = nextTitle;
    descriptionEl.textContent = nextDescription;
    linkEl.setAttribute("href", nextHref);
    meta.classList.remove(META_SWITCHING_CLASS);
  }, 400);
}

function updateCenterFocus(splide: Splide) {
  const root = splide.root;
  const slides = root.querySelectorAll<HTMLElement>(".splide__slide");

  if (!slides.length) return;

  const rect = root.getBoundingClientRect();
  const centerX = rect.left + rect.width / 2;
  let best: HTMLElement | null = null;
  let bestDist = Infinity;

  for (let i = 0; i < slides.length; i += 1) {
    const slide = slides.item(i) as HTMLElement;
    const r = slide.getBoundingClientRect();
    if (r.width < 8 || r.height < 8) continue;
    const slideCenterX = r.left + r.width / 2;
    const d = Math.abs(slideCenterX - centerX);
    if (d < bestDist) {
      bestDist = d;
      best = slide;
    }
  }

  slides.forEach((slide: HTMLElement) => {
    slide.classList.remove("is-recipe-focus");
  });

  if (!best) {
    updateMeta(root, null);
    return;
  }

  best.classList.add("is-recipe-focus");
  updateMeta(root, best);
}

function updateMetaByIndex(splide: Splide, index: number, fallbackIndex: number) {
  const root = splide.root;
  const slide = splide.Components.Slides.getAt(index)?.slide as
    | HTMLElement
    | undefined;
  const fallbackSlide = root.querySelector<HTMLElement>(
    `.splide__slide[data-splide-index="${fallbackIndex}"]`
  );

  updateMeta(root, slide ?? fallbackSlide);
}

function mountRecipeSplide(root: HTMLElement) {
  const splide = new Splide(root, {
    autoplay: true,
    // interval: 4000,
    speed: 1200,
    pauseOnHover: false,
    pauseOnFocus: false,
    type: "loop",
    // perPage: 2,
    fixedWidth: "calc(100vw * 664 / 1440)",
    focus: "center",
    // trimSpace: false,
    pagination: false,
    // flickPower: 150,
    updateOnMove: true,
    waitForTransition: false,
    // dragMinThreshold: { mouse: 10, touch: 20 },
    breakpoints: {
      989: {
        fixedWidth: "calc(100vw * 272 / 400)",
      },
    },
  });

  const scheduleFocus = () => {
    requestAnimationFrame(() => updateCenterFocus(splide));
  };

  splide.on("move", (newIndex: number, _prevIndex: number, destIndex: number) => {
    updateMetaByIndex(splide, destIndex, newIndex);
  });
  splide.on("mounted moved resized updated", scheduleFocus);
  splide.mount();
  scheduleFocus();
}

export function initRecipeSplide() {
  document.querySelectorAll<HTMLElement>(SPLIDE_SELECTOR).forEach(mountRecipeSplide);
}
